/**
 * CountsFooter.jsx
 *
 * Live "N tools match so far" footer with top-5 category breakdown.
 * Reads wizard selections, debounces them by 200ms to avoid recomputing
 * on every keystroke / slider tick, then runs them through
 * countMatchesByCategory and renders the totals.
 *
 * Hidden on ENTRY, SUMMARY, and RESULTS — those steps don't need the
 * running count.
 *
 * Product data is loaded async on mount via ensureProductsLoaded(); a
 * single isReady flag triggers the re-render once the caches hydrate.
 * Until then countMatchesByCategory returns { total: 0, byCategory: {} },
 * so the footer renders "0 tools match so far" for ~one tick at boot.
 *
 * @module components/wizard/CountsFooter
 * @since 2026-05-15 (Group D Sub-Plan 1, Task D6)
 */

import { useEffect, useState } from 'react';
import { useDebounce } from '@hooks/useDebounce';
import { useWizard } from '@contexts/WizardContext';
import {
  countMatchesByCategory,
  ensureProductsLoaded,
  humaniseCategory,
} from '@services/wizardMatcher';
import { STEPS } from './wizardConstants';

const HIDDEN_STEPS = new Set([STEPS.ENTRY, STEPS.SUMMARY, STEPS.RESULTS]);

export default function CountsFooter() {
  const { selections, currentStep } = useWizard();
  const debounced = useDebounce(selections, 200);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    ensureProductsLoaded().then(() => {
      if (!cancelled) setIsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (HIDDEN_STEPS.has(currentStep)) return null;

  // isReady is referenced so React re-renders once products land; the
  // value of countMatchesByCategory below depends on the module-level
  // cache state, not on isReady directly.
  void isReady;

  const { total, byCategory } = countMatchesByCategory(debounced);
  const sortedCats = Object.entries(byCategory)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  return (
    <footer
      className="border-t border-n2 py-3 px-4 text-sm text-n6 bg-n1"
      aria-live="polite"
      role="status"
    >
      <span className="font-normal text-carbon-black">{total.toLocaleString()}</span>{' '}
      tools match so far
      {sortedCats.length > 0 && (
        <>
          <span className="mx-2 text-n4">&middot;</span>
          {sortedCats.map(([cat, n], i) => (
            <span key={cat}>
              {n} {humaniseCategory(cat)}
              {i < sortedCats.length - 1 && (
                <span className="mx-1.5 text-n4">&middot;</span>
              )}
            </span>
          ))}
        </>
      )}
    </footer>
  );
}
